import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Layers, Utensils, Zap, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';
import { toast } from 'sonner';

// Option 2 — wine glass + card payment (premium, fine-dining)
const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/habawy4t_CTA%20LANDING%20IMAGE%202.png';

const setupOptions = [
  'Traditional till/cash register',
  'Separate ordering and payment systems',
  'No system currently',
  'Looking to upgrade existing EPOS',
];

const dailyVolumeOptions = [
  'Under 50',
  '50 – 150',
  '150 – 300',
  '300 – 500',
  'Over 500',
  'Seasonal',
];

const benefits = [
  {
    icon: Layers,
    title: 'All-in-One Simplicity',
    body: 'One device. Complete control. Eliminate multiple systems with our integrated EPOS that handles ordering, payments and kitchen management seamlessly.',
  },
  {
    icon: Utensils,
    title: 'Hospitality Focused Features',
    body: 'Built for busy service. Realtime kitchen integration, table management and staff efficiency tools designed specifically for hospitality operations.',
  },
  {
    icon: Zap,
    title: 'Quick Set Up',
    body: 'Start serving better immediately. Our streamlined setup gets your EPOS operational quickly with full training and ongoing support included.',
  },
];

const Field = ({ label, required, children, testId }) => (
  <div className="mb-7" data-testid={testId}>
    <label className="block text-sm font-bold text-gray-800 mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const Radio = ({ name, value, checked, onChange, testId }) => (
  <label
    className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg border cursor-pointer transition-all duration-150 ${
      checked
        ? 'border-[#1F4E79] bg-[#1F4E79]/5 ring-1 ring-[#1F4E79]/20'
        : 'border-gray-200 bg-white hover:border-[#1F4E79]/40 hover:bg-gray-50'
    }`}
    data-testid={testId}
  >
    <input
      type="radio"
      name={name}
      value={value}
      checked={checked}
      onChange={() => onChange(value)}
      className="accent-[#1F4E79]"
    />
    <span className="text-sm text-gray-800">{value}</span>
  </label>
);

const GetStartedEpos = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    businessType: '',
    setup: '',
    daily: '',
    name: '',
    email: '',
    phone: '',
    company: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: typeof e === 'string' ? e : e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.businessType || !form.setup || !form.daily || !form.name || !form.email || !form.phone) {
      toast.error('Please complete all required fields.');
      return;
    }
    setSubmitting(true);
    // UI-only mockup — wire to backend when ready
    setTimeout(() => {
      toast.success("Thanks! We'll be in touch within 24 hours to arrange your free EPOS consultation.");
      setForm({ businessType: '', setup: '', daily: '', name: '', email: '', phone: '', company: '' });
      setSubmitting(false);
    }, 700);
  };

  return (
    <div className="min-h-screen flex flex-col relative" data-testid="get-started-epos-page">
      {/* Fixed full-screen background image */}
      <div
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        aria-hidden="true"
      />
      {/* Dark readability wash */}
      <div className="fixed inset-0 bg-gradient-to-br from-[#0a1929]/80 via-[#0a1929]/65 to-[#163a5f]/75" aria-hidden="true" />

      <div className="relative z-10 flex flex-col min-h-screen">
        <Header />
        <div className="h-20" />

        <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-8 pb-16">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/85 hover:text-white font-medium mb-8 transition-colors duration-200"
            data-testid="get-started-epos-back"
          >
            <ArrowLeft size={18} />
            Back
          </button>

          {/* Top header */}
          <div className="max-w-4xl mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ef5b25]/20 border border-[#ef5b25]/40 text-[#ffcba6] text-[11px] font-bold uppercase tracking-[0.15em] mb-5">
              Free Consultation — No Obligation
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08] tracking-tight mb-4" data-testid="gse-title">
              Transform Your<br />Hospitality Operations
            </h1>
            <p className="text-lg sm:text-xl text-white/90 leading-relaxed max-w-3xl font-medium">
              From order-taking to payment processing, streamline everything with our advanced EPOS systems. Request your free consultation and personalised quote.
            </p>
          </div>

          {/* Form + Benefits layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Form — left/center */}
            <form
              onSubmit={handleSubmit}
              className="lg:col-span-8 bg-white rounded-xl shadow-2xl p-7 sm:p-9"
              data-testid="gse-form"
            >
              {/* 1. Business type */}
              <Field label="1. What type of hospitality business do you operate?" required testId="gse-field-business">
                <input
                  type="text"
                  value={form.businessType}
                  onChange={update('businessType')}
                  placeholder="e.g. restaurant, café, bar, takeaway, food truck"
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none transition-all duration-150"
                  data-testid="gse-input-business"
                />
              </Field>

              {/* 2. Current setup */}
              <Field label="2. What's your current setup?" required testId="gse-field-setup">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {setupOptions.map((opt, i) => (
                    <Radio
                      key={opt}
                      name="setup"
                      value={opt}
                      checked={form.setup === opt}
                      onChange={update('setup')}
                      testId={`gse-setup-${i}`}
                    />
                  ))}
                </div>
              </Field>

              {/* 3. Daily volume */}
              <Field label="3. How many customers do you serve daily?" required testId="gse-field-daily">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {dailyVolumeOptions.map((opt, i) => (
                    <Radio
                      key={opt}
                      name="daily"
                      value={opt}
                      checked={form.daily === opt}
                      onChange={update('daily')}
                      testId={`gse-daily-${i}`}
                    />
                  ))}
                </div>
              </Field>

              {/* 4. Contact */}
              <div data-testid="gse-field-contact">
                <label className="block text-sm font-bold text-gray-800 mb-3">4. Contact Details</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={form.name}
                    onChange={update('name')}
                    placeholder="Contact name *"
                    className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none transition-all duration-150"
                    data-testid="gse-input-name"
                  />
                  <input
                    type="email"
                    value={form.email}
                    onChange={update('email')}
                    placeholder="Email address *"
                    className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none transition-all duration-150"
                    data-testid="gse-input-email"
                  />
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={update('phone')}
                    placeholder="Phone number *"
                    className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none transition-all duration-150"
                    data-testid="gse-input-phone"
                  />
                  <input
                    type="text"
                    value={form.company}
                    onChange={update('company')}
                    placeholder="Business name (optional)"
                    className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none transition-all duration-150"
                    data-testid="gse-input-company"
                  />
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting}
                className="group mt-7 inline-flex items-center gap-2.5 px-7 py-3.5 bg-[#1F4E79] text-white font-bold rounded-lg hover:bg-[#163a5f] hover:gap-3.5 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="gse-submit"
              >
                {submitting ? 'Sending…' : 'Request Consultation'}</button>

              <p className="text-xs text-gray-500 mt-4">
                <CheckCircle2 size={12} className="inline mr-1 text-emerald-600" />
                Your information is secure and used only to prepare your free EPOS consultation.
              </p>
            </form>

            {/* Glass-morphism benefits — right */}
            <aside className="lg:col-span-4 space-y-4 lg:pt-16" data-testid="gse-benefits">
              {benefits.map((b, i) => {
                const Icon = b.icon;
                return (
                  <div
                    key={i}
                    className="rounded-xl p-5 bg-white/10 backdrop-blur-md border border-white/20 shadow-xl hover:bg-white/15 transition-all duration-200"
                    data-testid={`gse-benefit-${i}`}
                  >
                    <div className="flex items-center gap-2.5 mb-2">
                      <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center border border-white/20">
                        <Icon size={18} className="text-white" strokeWidth={1.75} />
                      </div>
                      <h3 className="text-[15px] font-bold text-white leading-tight">{b.title}</h3>
                    </div>
                    <p className="text-[13px] text-white/85 leading-relaxed">{b.body}</p>
                  </div>
                );
              })}
            </aside>
          </div>
        </main>

        <Footer />
        <BackToTop />
      </div>
    </div>
  );
};

export default GetStartedEpos;
